import type { ReactNode } from "react";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";

type ButtonProps = {
  href: string;
  children: ReactNode;
  /** primary: ink fill. ghost: ink outline. night: foam fill for the dark act. signal: buoy orange, final CTA only. */
  variant?: "primary" | "ghost" | "night" | "signal";
  size?: "sm" | "md" | "lg";
  arrow?: "right" | "down" | "none";
  className?: string;
  onClick?: () => void;
};

const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-ink text-foam hover:bg-ink-soft",
  ghost:
    "border-2 border-ink text-ink hover:bg-ink/5",
  night:
    "bg-foam text-ink hover:bg-paper",
  signal:
    "bg-signal text-ink hover:bg-signal-deep hover:text-foam",
};

const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "h-10 px-5 text-sm",
  md: "h-12 px-6 text-[0.95rem]",
  lg: "h-14 px-8 text-base",
};

export function Button({
  href,
  children,
  variant = "primary",
  size = "md",
  arrow = "right",
  className = "",
  onClick,
}: ButtonProps) {
  const external = href.startsWith("http");
  return (
    <a
      href={href}
      onClick={onClick}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      className={`group inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-transform duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {children}
      {arrow === "right" && (
        <ArrowRight
          weight="bold"
          className="size-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:group-hover:translate-x-0"
          aria-hidden
        />
      )}
      {arrow === "down" && (
        <ArrowDown
          weight="bold"
          className="size-4 shrink-0 transition-transform duration-200 group-hover:translate-y-0.5 motion-reduce:group-hover:translate-y-0"
          aria-hidden
        />
      )}
    </a>
  );
}
